/**
 * Rewards Page
 * Comprehensive rewards and gamification system display
 */

import React from 'react';
import Navigation from '@/components/navigation';
import RewardsDashboard from '@/components/rewards-dashboard';
import { Card } from '@/components/ui/card';
import { Lightbulb, Clock, Heart, Brain, Zap } from 'lucide-react';

export default function Rewards() {
  const tips = [
    {
      icon: Clock,
      title: 'Start Your Day Right',
      description:
        'Drink a glass of water within 30 minutes of waking up to kickstart your metabolism.',
      color: 'bg-blue-100',
    },
    {
      icon: Brain,
      title: 'Boost Your Focus',
      description: 'Even mild dehydration can affect concentration. Keep water at your desk.',
      color: 'bg-green-100',
    },
    {
      icon: Heart,
      title: 'Support Your Heart',
      description: 'Proper hydration helps your heart pump blood more easily through your body.',
      color: 'bg-pink-100',
    },
    {
      icon: Zap,
      title: 'Energy Throughout the Day',
      description: 'Fatigue is often an early sign of dehydration. Stay ahead of it!',
      color: 'bg-yellow-100',
    },
  ];

  const reminders = [
    'Morning: Start with a glass (250ml)',
    'Mid-morning: Second glass before lunch',
    'Lunch: Third glass with your meal',
    'Afternoon: Fourth glass around 3 PM',
    'Evening: Fifth glass before dinner',
    'Night: Final glass before bed',
  ];

  return (
    <div className="min-h-screen pb-24 bg-hydro-100">
      <div className="max-w-md mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-deep-800 mb-2 font-kumbh">Content</h1>
          <p className="text-deep-500 font-kumbh">Tips and insights for better hydration</p>
        </div>

        {/* Featured Tip */}
        <Card className="p-6 mb-6 bg-gradient-to-br from-lilac-200 to-purple-100 border-none">
          <div className="flex items-start gap-4">
            <Lightbulb className="w-8 h-8 text-purple-600 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-lg text-deep-800 mb-2 font-kumbh">Did you know?</h3>
              <p className="text-sm text-deep-700 font-kumbh">
                Your body is about 60% water. Staying hydrated helps regulate body temperature,
                transport nutrients, and maintain organ function. Corporate employees often forget
                to drink water during busy work hours - that's why SWYG App is here to help!
              </p>
            </div>
          </div>
        </Card>

        {/* Tips Grid */}
        <div className="space-y-4 mb-6">
          <h2 className="font-semibold text-deep-800 text-lg font-kumbh">Hydration Tips</h2>
          {tips.map((tip, index) => (
            <Card key={index} className={`p-5 border-none ${tip.color}`}>
              <div className="flex items-start gap-4">
                <div className="p-2 bg-white/50 rounded-xl">
                  <tip.icon className="w-5 h-5 text-deep-800" />
                </div>
                <div>
                  <h3 className="font-semibold text-deep-800 mb-1 font-kumbh">{tip.title}</h3>
                  <p className="text-sm text-deep-600 font-kumbh">{tip.description}</p>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Reminder Schedule */}
        <Card className="p-6">
          <h3 className="font-semibold text-deep-800 mb-4 font-kumbh">Suggested Daily Schedule</h3>
          <div className="space-y-3">
            {reminders.map((reminder, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-semibold text-purple-600 font-kumbh">
                    {index + 1}
                  </span>
                </div>
                <p className="text-sm text-deep-700 font-kumbh">{reminder}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 bg-lilac-100 rounded-lg">
            <p className="text-xs text-deep-500 text-center font-kumbh">
              💡 Tip: Set reminders on your phone to make hydration a habit!
            </p>
          </div>
        </Card>
      </div>

      <Navigation />
    </div>
  );
}
